import { MapMouseEvent } from 'mapbox-gl';
import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import useMap from '../../hooks/useMap';
import { featureLayers } from '../../utils/layers';

type EventType = 'click' | 'mousedown' | 'mouseenter' | 'mouseleave' | 'mousemove';
// T can't be used in the signature because MapMouseEvent is not generic and we don't want to override the mapbox type but it is used in the implementation
// eslint-disable-next-line unused-imports/no-unused-vars
type EventHandler<T extends EventType> = (ev: MapMouseEvent) => void;
type BaseMapEventType = 'click' | 'mousedown' | 'mouseup' | 'mousemove';
// T can't be used in the signature because MapMouseEvent is not generic and we don't want to override the mapbox type but it is used in the implementation
// eslint-disable-next-line unused-imports/no-unused-vars
type BaseMapEventHandler<T extends BaseMapEventType> = (event: MapMouseEvent) => void;
type BaseMapEventHandlers = Record<BaseMapEventType, { handler: BaseMapEventHandler<BaseMapEventType>; allLayers?: boolean }>;

type MapLayerInteractionsContextType = {
  registerEventHandler: <T extends EventType>(layerId: string, eventType: T, handler: EventHandler<T>) => void;
  unregisterEventHandler: <T extends EventType>(layerId: string, eventType: T, handler: EventHandler<T>) => void;
  registerBaseMapEventHandlers: (handlers: Partial<BaseMapEventHandlers>) => void;
  unregisterBaseMapEventHandlers: (eventTypes: BaseMapEventType[]) => void;
  /**
   * Call this when an overlay (popup, modal, etc.) is active and should block map layer events.
   * This prevents POI pins and other layers from receiving hover/click events while the user
   * is interacting with the overlay.
   *
   * @param overlayId - A unique identifier for the overlay (e.g., 'trail-popup', 'poi-popup').
   *                    Using IDs protects against duplicate calls and bugs in useEffect cleanup
   * @param active - Whether the overlay is becoming active (true) or inactive (false).
   */
  setOverlayActive: (overlayId: string, active: boolean) => void;
};

export const MapLayerInteractionsContext = createContext<MapLayerInteractionsContextType>({
  registerEventHandler: () => {},
  unregisterEventHandler: () => {},
  registerBaseMapEventHandlers: () => {},
  unregisterBaseMapEventHandlers: () => {},
  setOverlayActive: () => {}
});

type EventHandlersByLayer = {
  [layerId: string]: EventHandler<EventType>;
};

type MapLayerInteractionsProps = {
  children: ReactNode;
};

const isBaseMapEvent = (map: MapRef, event: MapMouseEvent) => {
  const features = map.queryRenderedFeatures(event.point);

  // If the click was on a feature in one of the feature layers don't consider this a base map event
  return features.length === 0 || !features.some(feature => feature.layer?.id && featureLayers.includes(feature.layer.id));
};

/**
 * When registering event handlers for layers in mapbox, you must pass in all layer ids that the event should care about,
 * otherwise, there is no way to determine which layer the event should actually fire on. For example, if layer A and layer B
 * both were to register a click handler with just their ids and a user clicked on a spot where A and B overlap, it would fire both handlers.
 * This wrapper component is designed to avoid this issue by consolidating all event handler logic into one callback
 * where the handler is aware of all layers - this allows it to fire only the "top" layer's handler.
 *
 * When a layer registers a event handler using the MapLayerInteractionsContext, it gets added to a record of handlers for that event type.
 * The layer id is used as the key to this record so that when that event type is fired, we can grab the top layer's id and trigger the
 * callback that was registered by that layer. The "top" layer is determined by the list of layer ids in utils/layers.ts, which acts as the
 * source of truth for the helper that sets the layer order in mapbox, ensuring consistency between what we consider the "top" and what the user sees.
 * A layer is also expected to unregister the event handler when it is unmounted or no longer needed.
 *
 * We also support registering event handlers for the base map itself rather than a specific layer with an option to trigger the handler on all layers.
 * This option is useful for events like mouseup, where the event may start on the base map but end on a layer.
 * For now only one handler per event type can be registered for the base map at a time to prevent needing to register for all events all the time.
 * If this becomes a limitation then we can refactor to support multiple handlers per event type but will need to take care to minimize the frequency with which we register/unregister handlers.
 *
 * The registry is per provider, and the provider binds to whichever map `useMap` resolves to. A tree
 * that renders map components on a secondary map (the print preview) nests its own provider inside
 * that tree's MapIdProvider, so the two maps keep separate registries and a layer id used on both
 * cannot have one map's handlers displace the other's.
 *
 * @param children components to render inside the context provider
 * @returns children, wrapped with the context provider
 */
const MapLayerInteractionsProvider = ({ children }: MapLayerInteractionsProps) => {
  const map = useMap();

  const layerEventHandlers = useRef<Record<EventType, EventHandlersByLayer>>({
    click: {},
    mousedown: {},
    mouseenter: {},
    mouseleave: {},
    mousemove: {}
  });
  const mouseLeaveTopLayerId = useRef('');

  // Track active overlays by ID. When the set is non-empty, map layer events should be blocked.
  // Using IDs protects against duplicate calls and bugs in useEffect cleanup.
  const activeOverlays = useRef<Set<string>>(new Set());

  // Since we only support one handler per event type for the base map we can store this in state instead of a ref
  // This allows us to only register for mapbox events for which a handler is provided instead of always registering for all events
  const [baseMapEventHandlers, setBaseMapEventHandlers] = useState<BaseMapEventHandlers>({} as BaseMapEventHandlers);

  // Register layer click handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const eventHandler: EventHandler<'click'> = event => {
      if (activeOverlays.current.size > 0) {
        return;
      }
      const topLayerId = event.features?.[0]?.layer?.id;

      if (topLayerId) {
        layerEventHandlers.current.click[topLayerId]?.(event);
      }
    };

    map.on('click', featureLayers, eventHandler);

    return () => {
      map.off('click', featureLayers, eventHandler);
    };
  }, [map]);

  // Register layer mousedown handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const eventHandler: EventHandler<'mousedown'> = event => {
      if (activeOverlays.current.size > 0) {
        return;
      }
      const topLayerId = event.features?.[0]?.layer?.id;
      if (topLayerId) {
        layerEventHandlers.current.mousedown[topLayerId]?.(event);
      }
    };

    map.on('mousedown', featureLayers, eventHandler);

    return () => {
      map.off('mousedown', featureLayers, eventHandler);
    };
  }, [map]);

  // Register layer mousemove handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const eventHandler: EventHandler<'mousemove'> = event => {
      if (activeOverlays.current.size > 0) {
        return;
      }
      const topLayerId = event.features?.[0]?.layer?.id;
      if (topLayerId) {
        layerEventHandlers.current.mousemove[topLayerId]?.(event);

        const previousMouseLeaveTopLayerId = mouseLeaveTopLayerId.current;
        if (previousMouseLeaveTopLayerId && previousMouseLeaveTopLayerId !== topLayerId) {
          // While the mouse hasn't left a layer, it is now hovered over a higher layer, so trigger the mouseleave handler of the previous top layer
          layerEventHandlers.current.mouseleave[previousMouseLeaveTopLayerId]?.(event);
        }
        mouseLeaveTopLayerId.current = topLayerId;
      }
    };

    map.on('mousemove', featureLayers, eventHandler);

    return () => {
      map.off('mousemove', featureLayers, eventHandler);
    };
  }, [map]);

  // Register layer mouseenter handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const eventHandler: EventHandler<'mouseenter'> = event => {
      if (activeOverlays.current.size > 0) {
        return;
      }
      const topLayerId = event.features?.[0]?.layer?.id;
      if (topLayerId) {
        layerEventHandlers.current.mouseenter[topLayerId]?.(event);
      }
    };

    map.on('mouseenter', featureLayers, eventHandler);

    return () => {
      map.off('mouseenter', featureLayers, eventHandler);
    };
  }, [map]);

  // Register layer mouseleave handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const eventHandler: EventHandler<'mouseleave'> = event => {
      const topLayerId = mouseLeaveTopLayerId.current;
      if (topLayerId) {
        layerEventHandlers.current.mouseleave[topLayerId]?.(event);
      }
      mouseLeaveTopLayerId.current = '';
    };

    map.on('mouseleave', featureLayers, eventHandler);

    return () => {
      map.off('mouseleave', featureLayers, eventHandler);
    };
  }, [map]);

  // Register base map event handlers
  useEffect(() => {
    if (!map) {
      return;
    }

    const handlers = Object.keys(baseMapEventHandlers).reduce((acc, eventType) => {
      const { handler, allLayers } = baseMapEventHandlers[eventType];
      const wrapper = (event: MapMouseEvent) => {
        // We want some events to trigger on all layers if those events represent completing an action that was started on the base map
        // (e.g. a mouseup event over a layer that started with a mousedown event on the base map)
        if (!isBaseMapEvent(map, event) && !allLayers) {
          return;
        }

        handler?.(event);
      };

      acc[eventType] = wrapper;
      return acc;
    }, {});

    Object.keys(handlers).forEach(eventType => {
      map.on(eventType as BaseMapEventType, handlers[eventType as BaseMapEventType]);
    });

    return () => {
      Object.keys(handlers).forEach(eventType => {
        map.off(eventType as BaseMapEventType, handlers[eventType as BaseMapEventType]);
      });
    };
  }, [map, baseMapEventHandlers]);

  const registerEventHandler = useCallback<MapLayerInteractionsContextType['registerEventHandler']>((layerId, eventType, handler) => {
    const handlers: EventHandlersByLayer = layerEventHandlers.current[eventType];
    handlers[layerId] = handler;
  }, []);

  // Only ever removes the registration it owns, so two components sharing a layer id cannot clobber
  // each other's cleanup. Cross-map interference is prevented by giving each map its own provider,
  // not by this guard.
  const unregisterEventHandler = useCallback<MapLayerInteractionsContextType['unregisterEventHandler']>((layerId, eventType, handler) => {
    const handlers: EventHandlersByLayer = layerEventHandlers.current[eventType];
    if (handlers[layerId] === handler) {
      delete handlers[layerId];
    } else if (handlers[layerId] && process.env.NODE_ENV !== 'production') {
      // Passing a different function than was registered leaves the handler live for the rest of
      // the session, which is silent and looks like the layer is still interactive after teardown
      console.warn(`Unregistering '${eventType}' on layer '${layerId}' with a handler that was never registered for it.`);
    }
  }, []);

  const registerBaseMapEventHandlers = useCallback<MapLayerInteractionsContextType['registerBaseMapEventHandlers']>(handlers => {
    setBaseMapEventHandlers(currentBaseMapEventHandlers => ({ ...currentBaseMapEventHandlers, ...handlers }));
  }, []);

  const unregisterBaseMapEventHandlers = useCallback<MapLayerInteractionsContextType['unregisterBaseMapEventHandlers']>(eventTypes => {
    setBaseMapEventHandlers(currentBaseMapEventHandlers => {
      const newHandlers = { ...currentBaseMapEventHandlers };
      eventTypes.forEach(eventType => {
        delete newHandlers[eventType];
      });
      return newHandlers;
    });
  }, []);

  const setOverlayActive = useCallback<MapLayerInteractionsContextType['setOverlayActive']>((overlayId, active) => {
    if (active) {
      activeOverlays.current.add(overlayId);
    } else {
      activeOverlays.current.delete(overlayId);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ registerEventHandler, unregisterEventHandler, registerBaseMapEventHandlers, unregisterBaseMapEventHandlers, setOverlayActive }),
    [registerEventHandler, unregisterEventHandler, registerBaseMapEventHandlers, unregisterBaseMapEventHandlers, setOverlayActive]
  );
  return <MapLayerInteractionsContext.Provider value={contextValue}>{children}</MapLayerInteractionsContext.Provider>;
};

export default MapLayerInteractionsProvider;
